import { useMemo } from 'react';
import { Model, ISurveyElement } from 'survey-core';

import SurveyTheme from './SurveyTheme';
import { ReadOnlySurveyTheme } from './ReadOnlySurveyTheme';

import 'survey-core/survey-core.min.css';
import './SurveyReadonlyStyles.css';
import './SurveyJsEquipStyles.css';

const getCustomSurveyStyles = (isReadOnly: boolean) => {
  if (isReadOnly) {
    return {
      body: 'sd-body-readonly',
      page: {
        root: 'survey-readonly',
      },
    };
  }
  return {};
};

const isElementVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return rect.height > 0 && rect.width > 0;
};

interface UseSurveyJSProps {
  isReadOnly?: boolean;
  templateSchema: unknown; // json object. should be Record<string, unknown>
  jsonData: unknown; // object
  onSurveyLoad?: (data: Model) => void;
  onValueChanged?: (data: Model) => void;
}

export const createSurveyJSModel = ({
  isReadOnly,
  onSurveyLoad,
  templateSchema,
  onValueChanged,
  jsonData,
}: UseSurveyJSProps) => {
  const surveyModel = new Model(templateSchema);
  const theme = isReadOnly ? ReadOnlySurveyTheme : SurveyTheme;
  surveyModel.applyTheme(theme);
  surveyModel.data = jsonData;
  surveyModel.css = getCustomSurveyStyles(isReadOnly);
  surveyModel.sendResultOnPageNext = true;
  surveyModel.showCompletedPage = false;
  surveyModel.showNavigationButtons = false;
  surveyModel.showTitle = false;
  surveyModel.textUpdateMode = 'onTyping';
  surveyModel.focusFirstQuestionAutomatic = false;

  // Disable automatic scroll to first error on validation
  surveyModel.focusOnFirstError = false;

  // Override scrollElementToTop to only scroll to FIRST error and filter readonly elements
  let hasScrolledToError = false;
  let scrollResetTimeout: NodeJS.Timeout | null = null;
  let lastValueChangeTime = 0;

  // Track value changes to distinguish between auto-validation and manual validation
  surveyModel.onValueChanged.add(() => {
    lastValueChangeTime = Date.now();
  });

  surveyModel.scrollElementToTop = (
    elementOrOptions: ISurveyElement | unknown,
    ..._args: unknown[]
  ): HTMLElement | null => {
    const isOptionsObject =
      _args.length === 0 &&
      elementOrOptions &&
      typeof elementOrOptions === 'object' &&
      'element' in elementOrOptions;
    const surveyElement = isOptionsObject
      ? (elementOrOptions as { element: ISurveyElement }).element
      : (elementOrOptions as ISurveyElement);

    // Heuristic: treat scroll requests that occur shortly after a value change as auto-validation.
    // This helps prevent jarring auto-scroll while the user is actively editing.
    const recentEditWindowMs = 1000;
    const timeSinceLastChange = Date.now() - lastValueChangeTime;
    const allowScroll = timeSinceLastChange > recentEditWindowMs;

    if (!allowScroll) {
      return null;
    }

    // Only process the first scroll call per validation
    if (hasScrolledToError) {
      return null;
    }

    if (surveyElement && surveyElement.name) {
      const allElements = document.querySelectorAll(
        `[data-name="${surveyElement.name}"]`,
      );

      const firstEditableElement = Array.from(allElements).find((el) => {
        const htmlEl = el as HTMLElement;
        if (
          htmlEl.classList.contains('sd-question--readonly') ||
          htmlEl.closest('.sd-body-readonly')
        ) {
          return false;
        }
        return isElementVisible(htmlEl);
      }) as HTMLElement | undefined;

      if (firstEditableElement) {
        hasScrolledToError = true;

        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement !== document.body) {
          activeElement.blur();
        }

        requestAnimationFrame(() => {
          firstEditableElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });

        // Reset after validation completes
        const validationCompleteDelayMs = 1000;
        if (scrollResetTimeout) {
          clearTimeout(scrollResetTimeout);
        }
        scrollResetTimeout = setTimeout(() => {
          hasScrolledToError = false;
        }, validationCompleteDelayMs);

        return firstEditableElement;
      }
    }

    return null;
  };

  if (onValueChanged) {
    surveyModel.onValueChanged.add(onValueChanged);
  }

  if (onSurveyLoad) {
    onSurveyLoad(surveyModel);
  }

  if (isReadOnly) {
    surveyModel.mode = 'display';
    surveyModel.getAllQuestions(true).forEach((question) => {
      if (question.isEmpty()) {
        question.visibleIf = 'false';
      }
    });
  }

  return surveyModel;
};

export const useSurveyJS = ({
  jsonData,
  templateSchema,
  onSurveyLoad,
  isReadOnly,
}: UseSurveyJSProps): {
  model: Model;
} => {
  const jsonDataString = useMemo(() => JSON.stringify(jsonData), [jsonData]);

  const model = useMemo<Model>(() => {
    return createSurveyJSModel({
      templateSchema,
      isReadOnly,
      onSurveyLoad,
      jsonData,
    });
  }, [templateSchema, isReadOnly, jsonDataString, onSurveyLoad]);

  return { model };
};
